import React, { useEffect, useState, useRef } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ROLES = ["president", "tresorier", "conseil", "coproprietaire", "gardien"];
const LANGS = ["fr", "ar", "en"];

// ------- Modale de création de compte -------
export function CreateUserModal({ onClose, onDone, t }) {
  const [form, setForm] = useState({ name: "", phone: "", apartment: "", email: "", role: "coproprietaire", lang: "fr", identifier: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (form.name.trim().length < 2) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/auth/users/suggest-identifier?name=${encodeURIComponent(form.name)}`); setSuggestions(data.suggestions); }
      catch { setSuggestions([]); }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [form.name]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error(t("name_required")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/users", form);
      toast.success(t("user_created"));
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("login_error")); }
    finally { setBusy(false); }
  };

  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  const roleOpts = ROLES.map((r) => ({ value: r, label: t("role_" + r) }));
  const langOpts = LANGS.map((l) => ({ value: l, label: t("lang_name_" + l) }));
  return (
    <Modal title={t("add_user")} onClose={onClose} testid="create-user-modal">
      <div className="space-y-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("full_name")} data-testid="cu-name" className={field} />
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="cu-suggestions">
            <span className="text-xs text-muted-foreground self-center">{t("suggested_id")}:</span>
            {suggestions.slice(0, 4).map((s) => (
              <button key={s} onClick={() => setForm({ ...form, identifier: s })}
                className={`text-xs font-mono px-2 py-1 rounded-lg ${form.identifier === s ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"}`}>{s}</button>
            ))}
          </div>
        )}
        <input value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value.toLowerCase() })} placeholder={t("identifier_optional")} data-testid="cu-identifier" className={`${field} font-mono`} />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("phone_ph")} data-testid="cu-phone" className={field} />
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("email_invite_optional")} data-testid="cu-email" className={field} />
        <input value={form.apartment} onChange={(e) => setForm({ ...form, apartment: e.target.value })} placeholder={t("apartment_label")} data-testid="cu-apartment" className={field} />
        <Select label={t("role_label")} value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={roleOpts} testid="cu-role" />
        <Select label={t("lang_label")} value={form.lang} onChange={(v) => setForm({ ...form, lang: v })} options={langOpts} testid="cu-lang" />
        <button onClick={submit} disabled={busy} data-testid="cu-submit" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? t("loading") : t("create_user")}</button>
      </div>
    </Modal>
  );
}

// ------- Modale de modification -------
export function EditUserModal({ target, onClose, onDone, t }) {
  const [form, setForm] = useState({ name: target.name, phone: target.phone || "", apartment: target.apartment || "", identifier: target.identifier, lang: target.lang || "fr" });
  const [role, setRole] = useState(target.role);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/auth/users/${target.id}`, form);
      if (role !== target.role) {
        const body = reason.trim() ? { role, reason, force: true } : { role };
        const { data } = await api.put(`/auth/users/${target.id}/role`, body);
        if (data?.pending) toast.info(t("role_pending"));
        else toast.success(t("profile_updated"));
      } else {
        toast.success(t("profile_updated"));
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || t("login_error")); }
    finally { setBusy(false); }
  };

  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  const roleOpts = ROLES.map((r) => ({ value: r, label: t("role_" + r) }));
  const langOpts = LANGS.map((l) => ({ value: l, label: t("lang_name_" + l) }));
  const treasurerTouched = (role === "tresorier" || target.role === "tresorier") && role !== target.role;
  return (
    <Modal title={t("edit")} onClose={onClose} testid="edit-user-modal">
      <div className="space-y-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("full_name")} data-testid="eu-name" className={field} />
        <input value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value.toLowerCase() })} placeholder={t("login_id_ph")} data-testid="eu-identifier" className={`${field} font-mono`} />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("phone_ph")} data-testid="eu-phone" className={field} />
        <input value={form.apartment} onChange={(e) => setForm({ ...form, apartment: e.target.value })} placeholder={t("apartment_label")} data-testid="eu-apartment" className={field} />
        <Select label={t("role_label")} value={role} onChange={setRole} options={roleOpts} testid="eu-role" />
        {treasurerTouched && (
          <div className="rounded-xl bg-warning/10 p-3">
            <p className="text-xs text-warning font-semibold mb-2 flex items-center gap-1.5"><Icons.ShieldAlert className="h-4 w-4" /> {t("treasurer_4eyes_note")}</p>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("force_reason_ph")} data-testid="eu-force-reason" className={field} />
          </div>
        )}
        <Select label={t("lang_label")} value={form.lang} onChange={(v) => setForm({ ...form, lang: v })} options={langOpts} testid="eu-lang" />
        <button onClick={save} disabled={busy} data-testid="eu-submit" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? t("loading") : t("save")}</button>
      </div>
    </Modal>
  );
}

// ------- Modale de validation d'une inscription libre (président) -------
export function ApproveRegistrationModal({ target, onClose, onDone, t }) {
  const [role, setRole] = useState(target.role || "coproprietaire");
  const [apartment, setApartment] = useState(target.apartment || "");
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/auth/registrations/${target.id}/approve`, { role, apartment });
      toast.success(t("registration_approved"));
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || t("login_error")); }
    finally { setBusy(false); }
  };

  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  const roleOpts = ROLES.map((r) => ({ value: r, label: t("role_" + r) }));
  return (
    <Modal title={t("approve_registration")} onClose={onClose} testid="approve-reg-modal">
      <div className="space-y-3">
        <div className="rounded-xl bg-secondary/50 p-3 text-sm">
          <p className="font-bold">{target.name}</p>
          <p className="text-muted-foreground">@{target.identifier} · {target.email}</p>
          <p className="text-muted-foreground">{target.phone || "—"}</p>
        </div>
        <label className="block">
          <span className="text-xs text-muted-foreground">{t("apartment_label")}</span>
          <input value={apartment} onChange={(e) => setApartment(e.target.value)} data-testid="ar-apartment" className={`${field} mt-1`} />
        </label>
        <Select label={t("role_label")} value={role} onChange={setRole} options={roleOpts} testid="ar-role" />
        <button onClick={approve} disabled={busy} data-testid="ar-submit" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? t("loading") : t("approve")}</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-card text-foreground rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{title}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-12 rounded-xl border border-input bg-background px-4 outline-none mt-1">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
